import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, fmtINR, downloadFile } from "../lib/api";
import { useAuth, can } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple, Power, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  product_category: "", product_type: "", service_type: "", part_name: "",
  mrp: "", amount: "", platform_fee_type: "percent", platform_fee_value: "",
  set_by: "fee", partner_rate: "",
};

// Mirror of backend computation for live preview
const feeAmount = (cr, type, val) => {
  cr = Number(cr) || 0; val = Number(val) || 0;
  return type === "percent" ? Math.round((cr * val / 100) * 100) / 100 : Math.round(val * 100) / 100;
};
const computePartnerRate = (cr, type, val) => Math.max(0, Math.round(((Number(cr) || 0) - feeAmount(cr, type, val)) * 100) / 100);

export default function Ratecards() {
  const { user } = useAuth();
  const editable = can(user, ["admin", "ops"]);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/ratecards", { params: { search, category: catFilter === "all" ? "" : catFilter } })
      .then((r) => setRows(r.data));
  }, [search, catFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/service-types").then((r) => setServiceTypes(r.data));
  }, []);

  const productTypes = categories.find((c) => c.name === form.product_category)?.product_types || [];

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      ...EMPTY, ...r,
      mrp: r.mrp ?? "",
      amount: r.amount ?? "",
      platform_fee_type: r.platform_fee_type || "percent",
      platform_fee_value: r.platform_fee_value ?? "",
      partner_rate: r.partner_rate ?? "",
      set_by: "fee",
    });
    setEditId(r.id); setOpen(true);
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const downloadTemplate = async () => {
    try { await downloadFile("/ratecards/import-template", "ratecard_import_template.xlsx"); }
    catch { toast.error("Could not download template"); }
  };
  const exportRatecards = async () => {
    try { await downloadFile("/ratecards/export", "service_ratecards.xlsx"); }
    catch { toast.error("Could not export ratecards"); }
  };
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/ratecards/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created, updated, skipped, errors } = r.data;
      toast.success(`Imported: ${created} created, ${updated} updated${skipped ? `, ${skipped} skipped` : ""}`);
      if (errors?.length) toast.error(`${errors.length} row error(s): ${errors.slice(0, 2).join("; ")}`);
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    setImporting(false);
  };

  const save = async () => {
    if (!form.product_category || !form.product_type || !form.service_type || form.amount === "") {
      toast.error("Category, Product Type, Service Type and Customer Rate are required");
      return;
    }
    try {
      const payload = {
        product_category: form.product_category,
        product_type: form.product_type,
        service_type: form.service_type,
        part_name: form.part_name || "",
        mrp: form.mrp === "" ? null : Number(form.mrp),
        amount: Number(form.amount),
        set_by: form.set_by,
      };
      if (form.set_by === "partner") {
        payload.partner_rate = form.partner_rate === "" ? 0 : Number(form.partner_rate);
      } else {
        payload.platform_fee_type = form.platform_fee_type || "percent";
        payload.platform_fee_value = form.platform_fee_value === "" ? 0 : Number(form.platform_fee_value);
      }
      if (editId) await api.put(`/ratecards/${editId}`, payload);
      else await api.post("/ratecards", payload);
      toast.success(`Ratecard ${editId ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = async (r) => {
    try {
      await api.post(`/ratecards/${r.id}/toggle-status`);
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader
        title="Ratecards"
        subtitle="Structured service pricing catalog"
        actions={editable && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImportFile} data-testid="ratecard-import-input" />
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={exportRatecards} data-testid="ratecard-export-btn">
              <DownloadSimple size={15} weight="bold" /> Export
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={downloadTemplate} data-testid="ratecard-template-btn">
              <DownloadSimple size={15} weight="bold" /> Template
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" disabled={importing}
              onClick={() => fileRef.current?.click()} data-testid="ratecard-import-btn">
              <UploadSimple size={15} weight="bold" /> {importing ? "Importing…" : "Import Excel"}
            </Button>
            <Button data-testid="add-ratecard-btn" onClick={openNew} className="rounded-sm gap-2">
              <Plus size={16} weight="bold" /> Add Ratecard
            </Button>
          </div>
        )}
      />
      <div className="p-8 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="ratecard-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category, type, part…" className="rounded-sm pl-9" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="rounded-sm w-52" data-testid="ratecard-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Product Category</TableHead>
                <TableHead>Product Type</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Part Name</TableHead>
                <TableHead className="text-right">MRP</TableHead>
                <TableHead className="text-right">Customer Rate</TableHead>
                <TableHead className="text-right">Platform Fee</TableHead>
                <TableHead className="text-right">Partner Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="ratecards-table">
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No ratecards found</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-secondary/30">
                  <TableCell className="font-semibold">{r.product_category}</TableCell>
                  <TableCell>{r.product_type}</TableCell>
                  <TableCell><span className="text-xs font-semibold uppercase tracking-wide bg-secondary px-2 py-1 rounded-sm">{r.service_type}</span></TableCell>
                  <TableCell className="text-muted-foreground">{r.part_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{r.mrp ? fmtINR(r.mrp) : "—"}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmtINR(r.amount)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {!r.platform_fee_value
                      ? "—"
                      : r.platform_fee_type === "percent"
                      ? `${r.platform_fee_value}%`
                      : fmtINR(r.platform_fee_value)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-primary font-semibold">{fmtINR(r.partner_rate ?? 0)}</TableCell>
                  <TableCell><StatusBadge value={r.status} /></TableCell>
                  <TableCell className="text-right">
                    {editable && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`ratecard-edit-${r.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" className={`h-8 w-8 ${r.status === "active" ? "text-destructive" : "text-green-600"}`}
                          onClick={() => toggle(r)} data-testid={`ratecard-toggle-${r.id}`}>
                          <Power size={16} />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="ratecard-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Ratecard" : "New Ratecard"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div>
              <Label className="label-caps">Product Category</Label>
              <Select value={form.product_category}
                onValueChange={(v) => setForm({ ...form, product_category: v, product_type: "" })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="ratecard-field-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Product Type</Label>
              <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}
                disabled={!form.product_category}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="ratecard-field-product-type"><SelectValue placeholder="Select product type" /></SelectTrigger>
                <SelectContent>
                  {productTypes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Service Type</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="ratecard-field-service-type"><SelectValue placeholder="Select service type" /></SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Part Name (optional)</Label>
              <Input data-testid="ratecard-field-part" value={form.part_name}
                onChange={(e) => setForm({ ...form, part_name: e.target.value })} placeholder="e.g. Compressor" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="label-caps">MRP (₹) — optional</Label>
              <Input data-testid="ratecard-field-mrp" type="number" value={form.mrp}
                onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="2499" className="rounded-sm mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">List price shown struck-through on the storefront.</p>
            </div>
            <div>
              <Label className="label-caps">Customer Rate (₹)</Label>
              <Input data-testid="ratecard-field-amount" type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="1799" className="rounded-sm mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">The actual price the customer pays.</p>
            </div>

            <div className="border-t border-border pt-4">
              <Label className="label-caps">Margin Setup (confidential)</Label>
              <Select value={form.set_by} onValueChange={(v) => setForm({ ...form, set_by: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="ratecard-field-setby"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fee">Enter Platform Fee (auto partner rate)</SelectItem>
                  <SelectItem value="partner">Enter Partner Rate (auto platform fee)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.set_by === "fee" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps">Fee Type</Label>
                  <Select value={form.platform_fee_type} onValueChange={(v) => setForm({ ...form, platform_fee_type: v })}>
                    <SelectTrigger className="rounded-sm mt-1.5" data-testid="ratecard-field-fee-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                      <SelectItem value="amount">Fixed (₹)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps">Fee Value</Label>
                  <Input data-testid="ratecard-field-fee-value" type="number" value={form.platform_fee_value}
                    onChange={(e) => setForm({ ...form, platform_fee_value: e.target.value })}
                    placeholder={form.platform_fee_type === "percent" ? "20" : "300"} className="rounded-sm mt-1.5" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="label-caps">Partner Rate (₹)</Label>
                <Input data-testid="ratecard-field-partner-rate" type="number" value={form.partner_rate}
                  onChange={(e) => setForm({ ...form, partner_rate: e.target.value })} placeholder="1499" className="rounded-sm mt-1.5" />
                <p className="text-xs text-muted-foreground mt-1">What the partner earns. Platform fee = Customer Rate − Partner Rate.</p>
              </div>
            )}

            {form.product_category && form.product_type && form.service_type && form.amount !== "" && (() => {
              const cr = Number(form.amount) || 0;
              const partnerRate = form.set_by === "partner"
                ? Math.max(0, Number(form.partner_rate) || 0)
                : computePartnerRate(cr, form.platform_fee_type, form.platform_fee_value);
              const fee = Math.max(0, Math.round((cr - partnerRate) * 100) / 100);
              return (
                <div className="bg-secondary rounded-sm p-3 text-sm space-y-1" data-testid="ratecard-preview">
                  <p className="label-caps mb-1">Preview</p>
                  <p className="font-medium">{form.product_category} · {form.product_type} · {form.service_type}{form.part_name ? ` · ${form.part_name}` : ""}</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Customer pays</span><span className="font-mono font-semibold">{fmtINR(cr)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Platform fee (Fixigo)</span><span className="font-mono">{fmtINR(fee)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Partner earns</span><span className="font-mono font-semibold text-primary">{fmtINR(partnerRate)}</span></div>
                </div>
              );
            })()}
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="ratecard-save">Save Ratecard</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
